// Pure helpers behind a Predict bug's own page (STRY0039090). The page reads
// GET /predict/bug: {bug: {vendor, bugId, source, title, url, filed, firstSeen,
// score, promoted, reason, weaknessType, cwe, frozenAt, scorerVersion, cve,
// target, targetName}}.

import { cveId } from './predictWatchlistModel';
import { WATCH_PATH } from './predictDashboardModel';

// A bug's address: /predict/watch/<vendor>/<bugId>, each part encoded, so an id
// with a slash or a '#' still makes one path segment. The router hands the
// page the decoded parts.
export const bugPagePath = (vendor, bugId) =>
  `${WATCH_PATH}/${encodeURIComponent(vendor)}/${encodeURIComponent(bugId)}`;

const DAY_MS = 24 * 60 * 60 * 1000;

const isText = value => typeof value === 'string' && value.trim() !== '';

// The wire's dates are ISO 8601 in UTC; an unreadable one is null.
const timeOf = iso => {
  if (!isText(iso)) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

// Whole days from one ISO time to another, rounded down. Null when either is unreadable.
export const daysBetween = (fromIso, toIso) => {
  const from = timeOf(fromIso);
  const to = timeOf(toIso);
  return from === null || to === null ? null : Math.floor((to - from) / DAY_MS);
};

const days = n => `${n} ${n === 1 ? 'day' : 'days'}`;

// How long since the bug's first score was kept: 'Less than a day', '1 day', '12 days'.
export const ageSinceFreeze = (frozenAt, now = new Date()) => {
  const n = daysBetween(frozenAt, now.toISOString());
  if (n === null) return '—';
  return n < 1 ? 'Less than a day' : days(n);
};

const HOUR_MS = 60 * 60 * 1000;

// How long a gap is: '3 days', '5 hours', 'Less than an hour'. Whole units, rounded down.
const span = ms => {
  if (ms >= DAY_MS) return days(Math.floor(ms / DAY_MS));
  const hours = Math.floor(ms / HOUR_MS);
  if (hours >= 1) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  return 'Less than an hour';
};

// The gap a timeline step shows after the step before it: '3 days later', '5 hours later'.
export const gapLabel = (fromIso, toIso) => {
  const from = timeOf(fromIso);
  const to = timeOf(toIso);
  if (from === null || to === null) return null;
  return to >= from ? `${span(to - from)} later` : `${span(from - to)} earlier`;
};

// The bug's life so far, oldest first: filed, first seen by BugZero, frozen
// (its first score kept for good), and the CVE it became when it has one. A
// step with no date is left out, except the CVE, which shows without one.
// `outcome` is its row from GET /predict/outcomes, when there is one.
export const timelineSteps = (bug, outcome = null) => {
  const steps = [
    { key: 'filed', label: 'Filed', at: bug.filed, note: 'On the project’s tracker' },
    { key: 'firstSeen', label: 'First seen', at: bug.firstSeen, note: 'When BugZero first saw it' },
    {
      key: 'frozen',
      label: bug.promoted ? 'Frozen and promoted' : 'Frozen',
      at: bug.frozenAt,
      note: 'Its first score, kept for good'
    }
  ].filter(step => timeOf(step.at) !== null);
  const cve = cveId(bug.cve) || (outcome && isText(outcome.cve) ? outcome.cve.trim() : null);
  if (cve) {
    steps.push({
      key: 'cve',
      label: `Became ${cve}`,
      at: outcome && timeOf(outcome.firstPublicAt) !== null ? outcome.firstPublicAt : null,
      note: 'Its first public statement'
    });
  }
  return steps;
};

// The scorer's CWE: 'CWE-416' (from 'cwe-416', '416' or 416) with its MITRE
// page, or any other text as it is with no link. Null for none.
export const cweOf = cwe => {
  const text = typeof cwe === 'number' && Number.isInteger(cwe) ? String(cwe) : isText(cwe) ? cwe.trim() : null;
  if (!text) return null;
  const m = /^(?:CWE-)?(\d{1,5})$/i.exec(text);
  return m
    ? { id: `CWE-${m[1]}`, url: `https://cwe.mitre.org/data/definitions/${m[1]}.html` }
    : { id: text, url: null };
};

// The outcome row for this bug, if GET /predict/outcomes has one.
export const outcomeFor = (outcomes, vendor, bugId) =>
  (Array.isArray(outcomes) ? outcomes : []).find(o => o && o.vendor === vendor && o.bugId === bugId) || null;
